import { EventEmitter as EventEmitter3 } from 'eventemitter3';
import global from '@Global';

class EventEmitterManager
{
    /**
     * Channels map
     * This is used to improve performance of the event emitter
     */
    private channels: {
        [channel: string]: EventEmitter;
    }

    constructor()
    {
        this.channels = {};
        global.eventEmitter = this;
    }

    /**
     * Crates a channel event emitter
     * @param channel
     */
    private createChannel(channel: string): void
    {
        if (this.channels[channel] instanceof EventEmitter3 === false)
        {
            this.channels[channel] = new EventEmitter3();
        }
    }

    /**
     * Return an array listing the events for which the emitter has registered listeners.
     * @param channel
     */
    public eventNames(channel: string): (string | symbol)[]
    {
        this.createChannel(channel);

        return this.channels[channel].eventNames();
    }

    /**
     * Return the listeners registered for a given channel's event.
     * @param event
     */
    public listeners(channel: string, event: string | symbol): EventEmitter.ListenerFn[]
    {
        this.createChannel(channel);

        return this.channels[channel].listeners(event);
    }

    /**
     * Return the number of listeners listening to a given channel's event.
     * @param channel
     * @param event
     */
    listenerCount(channel: string, event: string | symbol): number
    {
        this.createChannel(channel);

        return this.channels[channel].listenerCount(event);
    }

    /**
     * Calls each of the listeners registered for a given event.
     * @param channel
     * @param event
     * @param args
     */
    emit(channel: string, event: string | symbol, ...args: any[]): boolean
    {
        this.createChannel(channel);

        return this.channels[channel].emit(event, ...args);
    }

    /**
     * Add a listener for a given channel event.
     * @param channel
     * @param event
     * @param fn
     * @param context
     */
    on(channel: string, event: string | symbol, fn: EventEmitter.ListenerFn, context?: any): EventEmitter
    {
        this.createChannel(channel);

        return this.channels[channel].on(event, fn);
    }

    /**
     * Add a listener for a given channel event.
     * @param channel
     * @param event
     * @param fn
     * @param context
     */
    addListener(channel: string, event: string | symbol, fn: EventEmitter.ListenerFn, context?: any): EventEmitter
    {
        return this.on(channel, event, context);
    }

    /**
     * Add a one-time listener for a given event.
     * @param channel
     * @param event
     * @param fn
     * @param context
     */
    once(channel: string, event: string | symbol, fn: EventEmitter.ListenerFn, context?: any): EventEmitter
    {
        this.createChannel(channel);

        return this.channels[channel].once(event, fn);
    }


    /**
     * Remove the listeners of a given event.
     * @param event
     * @param fn
     * @param context
     * @param once
     */
    off(channel: string, event: string | symbol, fn?: EventEmitter.ListenerFn, context?: any, once?: boolean): EventEmitter
    {
        this.createChannel(channel);

        return this.channels[channel].off(event, fn, context, once);
    }

    /**
     * Remove the listeners of a given event.
     * @param channel
     * @param event
     * @param fn
     * @param context
     * @param once
     */
    removeListener(channel: string, event: string | symbol, fn?: EventEmitter.ListenerFn, context?: any, once?: boolean): EventEmitter
    {
        return this.off(channel, event, fn, context, once);
    }

    /**
     *
     * @param channel
     * @param event
     */
    removeAllListeners(channel: string, event?: string | symbol): EventEmitter
    {
        this.createChannel('channel');

        return this.channels[channel].removeAllListeners(event);
    }

}

export default new EventEmitterManager()